import React, { useEffect, useState } from 'react';

import { usePaymentReportingOptIn } from '@renter-hub/hooks/usePaymentReporting';
import type { RentCreditReportOptInWebApiRequestDto } from '@zg-rentals/ts-rent-payment-client';
import {
  Anchor,
  Box,
  Button,
  ButtonGroup,
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Icon,
  Input,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Paragraph,
  Radio,
  Select,
  Tag,
  Text,
  TextButton,
  Tooltip,
} from '@zillow/constellation-10';
import { IconWarningOutline } from '@zillow/constellation-icons';
import { DatarouterError } from '@zg-rentals/web-base/src/utils/DatarouterError';

import FaqComponent from '@common/components/ConstellationFaqComponent';
import * as S from './StyledComponent';
import CreditScoreImage from '@assets/creditScoreImage.png';
import faqs from '@renter-hub/constants/FaqConstants';
import type { RequestPaymentReportingOptInPayloadPartialInterface } from '@renter-hub/models/RequestsTypes';
import RachelCSEvents from '@renter-hub/tracking/ClickStreamEvents';
import { months } from '@common/paymentsUtils/dateUtils';
import { validateAllInputs } from './validation';
import { detectCombinedNames } from '@common/utils/detectCombinedNames';
import errorMessages from '@common/constants/errorMessages';

type Props = {
  fromDashboard: boolean;
  alreadyEnrolled: boolean | null;
  optInPayload: RequestPaymentReportingOptInPayloadPartialInterface;
  hasPaymentHistory: boolean;
  user: any;
  listingAlias: string;
  firstName: string;
  lastName: string;
  continueToNextPage: () => void;
  showSuccessNotification: () => void;
  showSuccessModal: () => void;
  goToDashboard: () => void;
  setCurrentTab?: (tabIndex: number) => void;
};

const PaymentReportingOptInPage: React.FC<React.PropsWithChildren<Props>> = (props: Props) => {
  const {
    fromDashboard,
    alreadyEnrolled,
    optInPayload,
    hasPaymentHistory,
    user,
    listingAlias,
    firstName,
    lastName,
    continueToNextPage,
    showSuccessNotification,
    showSuccessModal,
    goToDashboard,
    setCurrentTab,
  } = props;

  // React Query hook for payment reporting opt-in (replacing RSAA action)
  const optInMutation = usePaymentReportingOptIn();

  useEffect(() => {
    // Redirect user if they have opted in to payment reporting
    // isEnrolling determines whether we're in the confirmation flow and therefore shouldn't do this redirect
    if (!isEnrolling && alreadyEnrolled !== null && alreadyEnrolled !== undefined) {
      goToDashboard();
    }
  }, [alreadyEnrolled]);

  const initialDateOfBirth = {
    monthOfBirth: {
      value: '',
      error: '',
    },
    dayOfBirth: {
      value: '',
      error: '',
    },
    yearOfBirth: {
      value: '',
      error: '',
    },
  };

  const initialFirstName = {
    value: firstName || '',
    error: detectCombinedNames(firstName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER : '',
  };

  const initialLastName = {
    value: lastName || '',
    error: detectCombinedNames(lastName) ? errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER : '',
  };

  const [enrollmentError, setEnrollmentError] = useState(false);
  const [isDuplicateEnrollment, setIsDuplicateEnrollment] = useState(false);
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reportHistorical, setReportHistorical] = useState(true);
  const [dob, setDOB] = React.useState<Record<string, any>>(initialDateOfBirth);
  const [updatedFirstName, setFirstName] = useState(initialFirstName);
  const [updatedLastName, setLastName] = useState(initialLastName);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  // Used to ensure we redirect correctly to the payment info page
  // TODO SI-4217 to implement a cleaner solution
  const [isEnrolling, setIsEnrolling] = useState(false);

  const shortFaqs = faqs.PAYMENT_SETTINGS_V2.PAYMENT_REPORTING.body().slice(0, 5);
  const longFaqs = faqs.PAYMENT_SETTINGS_V2.PAYMENT_REPORTING.body();
  const onViewMoreFAQClick = () => {
    setFaqModalOpen(true);
    RachelCSEvents.emitOpenRentReportingFAQViewMoreEvent({ user, currentProperty: { listingAlias } });
  };

  const onSkipClick = () => {
    RachelCSEvents.emitSkipOptIntoRentReportingEvent({ user, currentProperty: { listingAlias }, fromDashboard });
    continueToNextPage();
  };

  const enrollBtnText = fromDashboard ? 'Enroll' : 'Enroll and continue';
  const skipBtnText = fromDashboard ? 'Cancel' : 'Skip';
  const skipButton = <Button onClick={onSkipClick}>{skipBtnText}</Button>;

  const enrollBtnDisabled =
    fromDashboard && hasPaymentHistory
      ? dob.yearOfBirth.value === '' ||
        dob.monthOfBirth.value === '' ||
        dob.dayOfBirth.value === '' ||
        updatedFirstName.value === '' ||
        updatedLastName.value === '' ||
        reportHistorical === undefined ||
        isLoading
      : dob.yearOfBirth.value === '' ||
        dob.monthOfBirth.value === '' ||
        dob.dayOfBirth.value === '' ||
        updatedFirstName.value === '' ||
        updatedLastName.value === '' ||
        isLoading;

  const submitAndContinue = async () => {
    if (!validateAllInputs({ props, state: { updatedFirstName, updatedLastName, dob }, setDOB })) {
      setIsConfirmationModalOpen(false);
      return;
    }
    setIsLoading(true);

    // Format dateOfBirth to MM/DD/YYYY with leading zeros from component state
    const formatDateOfBirth = (): string => {
      const month = String(dob.monthOfBirth.value).padStart(2, '0');
      const day = String(dob.dayOfBirth.value).padStart(2, '0');
      const year = dob.yearOfBirth.value;
      return `${month}/${day}/${year}`;
    };

    const transformedPayload: RentCreditReportOptInWebApiRequestDto = {
      ...optInPayload,
      dateOfBirth: formatDateOfBirth(),
      firstName: updatedFirstName.value,
      lastName: updatedLastName.value,
      middleName: '',
      reportHistorical: reportHistorical ?? false,
      shouldUpdatePaymentName: firstName !== updatedFirstName.value || lastName !== updatedLastName.value,
    };

    try {
      await optInMutation.mutateAsync({ postData: transformedPayload });
      if (!fromDashboard) {
        showSuccessNotification();
      } else {
        showSuccessModal();
      }
      // After successful enrollment, we want to show the rent reporting tab
      if (setCurrentTab) {
        setCurrentTab(1);
      }
      continueToNextPage();
    } catch (error) {
      if (DatarouterError.is(error) && error.code === 'duplicateEnrollment') {
        setIsDuplicateEnrollment(true);
      }
      setEnrollmentError(true);
      setIsConfirmationModalOpen(false);
    } finally {
      setIsLoading(false);
    }
    RachelCSEvents.emitOptIntoRentReportingEvent({ user, currentProperty: { listingAlias } });
  };

  const errorScreen = (
    <S.ErrorScreen id="error-screen">
      <Icon css={{ color: 'brandSecondary' }} size="md" render={<IconWarningOutline />} />
      <Heading level={2} textStyle="heading-xs">
        Rent reporting enrollment was unsuccessful
      </Heading>
      {isDuplicateEnrollment ? (
        <>
          <Text>
            You are already enrolled in CreditClimb for rent reporting. Contact{' '}
            <Anchor href="mailto:rentpayments@zillow.com">Customer Support</Anchor>.
          </Text>
          <S.ButtonGroup>
            <Button emphasis="filled" onClick={continueToNextPage}>
              Continue
            </Button>
          </S.ButtonGroup>
        </>
      ) : (
        <>
          <Text>An error occurred during enrollment for rent reporting. Please try again.</Text>
          <S.ButtonGroup>
            <Button emphasis="filled" onClick={() => setEnrollmentError(false)}>
              Retry enrollment
            </Button>
            {skipButton}
          </S.ButtonGroup>
        </>
      )}
    </S.ErrorScreen>
  );

  const renderMonthOptions = (): Array<JSX.Element> | null =>
    months.map((monthOption, index) => (
      <option key={monthOption} value={(index + 1).toString()}>
        {monthOption}
      </option>
    ));

  const updateDOBAndSave = (e, birthDate) => {
    const updatedDOB = {
      ...birthDate,
      [e.currentTarget.dataset.statekey]: {
        value: e.currentTarget.value,
        error: '',
      },
    };
    setDOB(updatedDOB);
  };

  const onInputChange = (e) => {
    updateDOBAndSave(e, dob);
  };

  const onFirstNameInputChange = (e) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER;
    }
    setFirstName({
      value: e.currentTarget.value || '',
      error,
    });
  };

  const onLastNameInputChange = (e) => {
    let error = '';
    if (detectCombinedNames(e.currentTarget.value)) {
      error = errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER;
    }
    setLastName({
      value: e.currentTarget.value || '',
      error,
    });
  };

  return (
    <>
      {!enrollmentError && (
        <S.PaymentReportingHeader>
          <div className="header-content">
            <div className="header-text">
              <Heading level={2} textStyle="heading-xs">
                Build your credit when you pay rent
              </Heading>
              <Tag tone="aqua">Free</Tag>
            </div>
            <Text textStyle="body">
              When you enroll, Zillow lets Experian, Equifax, and TransUnion know you paid rent.
            </Text>
            <Text>
              Consistent, on time payments could help improve your credit score in just a few months. (Don’t worry, late
              or missed payments are never reported.)
            </Text>
          </div>
          <div className="credit-score-image">
            <img src={CreditScoreImage as unknown as string} alt="" />
          </div>
        </S.PaymentReportingHeader>
      )}
      <S.ContentBody>
        <S.PaymentReportingForm>
          {!enrollmentError && (
            <>
              <Text textStyle="body-lg-bold">To enroll, enter your information</Text>
              <Text>
                We’ll use this to confirm your identity, so we can report payments in the right place.
                <Tooltip
                  css={{ maxWidth: '275px' }}
                  placement="top-end"
                  content="Please review and ensure accuracy of your legal name and date of birth so we can accurately report your payments to the credit bureaus."
                />
              </Text>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: { base: '1fr', md: '6fr 6fr' },
                  gap: '16px',
                  marginTop: '16px',
                  marginBottom: '16px',
                }}
              >
                <FormField
                  control={
                    <Input
                      css={{ padding: '10px 16px 10px 16px' }}
                      onChange={onFirstNameInputChange}
                      value={updatedFirstName.value}
                    />
                  }
                  error={!!updatedFirstName.error}
                  label={<Label>First name</Label>}
                  description={
                    updatedFirstName.error ? (
                      <FormHelp css={{ marginTop: '4px' }} error>
                        {updatedFirstName.error}
                      </FormHelp>
                    ) : null
                  }
                />

                <FormField
                  control={
                    <Input
                      css={{ padding: '10px 16px 10px 16px' }}
                      onChange={onLastNameInputChange}
                      value={updatedLastName.value}
                    />
                  }
                  error={!!updatedLastName.error}
                  label={<Label>Last name</Label>}
                  description={
                    updatedLastName.error ? (
                      <FormHelp css={{ marginTop: '4px' }} error>
                        {updatedLastName.error}
                      </FormHelp>
                    ) : null
                  }
                />
              </Box>
              <Box
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: '16px',
                }}
              >
                <Label>Date of birth</Label>
                <Box
                  css={{
                    display: 'grid',
                    gridTemplateColumns: '5fr 3fr 4fr',
                    gap: '16px',
                  }}
                >
                  <Select
                    css={{ fontFamily: 'token(fonts.ui)' }}
                    data-statekey="monthOfBirth"
                    value={dob.monthOfBirth.value}
                    onChange={onInputChange}
                  >
                    <option value="" />
                    {renderMonthOptions()}
                  </Select>
                  <Input
                    data-statekey="dayOfBirth"
                    css={{ padding: '10px 16px 10px 16px' }}
                    placeholder="DD"
                    maxLength={2}
                    value={dob.dayOfBirth.value}
                    onChange={onInputChange}
                  />
                  <Input
                    data-statekey="yearOfBirth"
                    css={{ padding: '10px 16px 10px 16px' }}
                    placeholder="YYYY"
                    maxLength={4}
                    value={dob.yearOfBirth.value}
                    onChange={onInputChange}
                  />
                </Box>
                {dob.dayOfBirth.error && (
                  <FormHelp css={{ marginTop: '4px' }} error>
                    {dob.dayOfBirth.error}
                  </FormHelp>
                )}
                {dob.monthOfBirth.error && (
                  <FormHelp css={{ marginTop: '4px' }} error>
                    {dob.monthOfBirth.error}
                  </FormHelp>
                )}
                {dob.yearOfBirth.error && (
                  <FormHelp css={{ marginTop: '4px' }} error>
                    {dob.yearOfBirth.error}
                  </FormHelp>
                )}
              </Box>
              {fromDashboard && hasPaymentHistory && (
                <FieldSet
                  legend={
                    <Legend css={{ textStyle: 'body-lg-bold' }}>
                      Looks like you’ve made payments in the past 24 months
                    </Legend>
                  }
                >
                  <Text>
                    To build credit faster, you can choose to report payments made on Zillow in the past 24 months.
                  </Text>
                  <LabeledControl
                    control={
                      <Radio
                        css={{ marginTop: '12px' }}
                        checked={reportHistorical}
                        onChange={() => setReportHistorical(true)}
                      />
                    }
                    label={<Label>Include my past payments (recommended)</Label>}
                  />
                  <LabeledControl
                    control={
                      <Radio
                        css={{ marginTop: '12px' }}
                        checked={reportHistorical === false}
                        onChange={() => setReportHistorical(false)}
                      />
                    }
                    label={<Label>Don’t include my past payments</Label>}
                  />
                </FieldSet>
              )}
              <Modal
                open={isConfirmationModalOpen}
                onOpenChange={(isOpen) => {
                  setIsEnrolling(true);
                  setIsConfirmationModalOpen(isOpen);
                }}
                size="md"
                header={
                  <Heading css={{ textAlign: 'center' }} level={5}>
                    Verify your information
                  </Heading>
                }
                body={
                  <>
                    <Heading level={5}>Verify that your information is correct</Heading>
                    <Paragraph textStyle="body" css={{ paddingTop: 'tighter' }}>
                      We’ll use this to confirm your identity, so we can accurately report your payments to the credit
                      bureaus.
                    </Paragraph>
                    <Paragraph textStyle="body-sm-bold" css={{ paddingTop: 'default' }}>
                      First name
                    </Paragraph>
                    <Paragraph textStyle="body-sm" css={{ paddingTop: 'tighter' }}>
                      {updatedFirstName.value}
                    </Paragraph>
                    {updatedFirstName.error && (
                      <Paragraph
                        textStyle="body-xs"
                        css={{ color: 'text.feedback.critical.strong', paddingTop: 'tighter' }}
                      >
                        {errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER}
                      </Paragraph>
                    )}
                    <Paragraph textStyle="body-sm-bold" css={{ paddingTop: 'default' }}>
                      Last name
                    </Paragraph>
                    <Paragraph textStyle="body-sm" css={{ paddingTop: 'tighter' }}>
                      {updatedLastName.value}
                    </Paragraph>
                    {updatedLastName.error && (
                      <Paragraph
                        textStyle="body-xs"
                        css={{ color: 'text.feedback.critical.strong', paddingTop: 'tighter' }}
                      >
                        {errorMessages.COMBINED_NAMES_ERROR_TEXT_RENTER}
                      </Paragraph>
                    )}
                    <Paragraph textStyle="body-sm-bold" css={{ paddingTop: 'default' }}>
                      Date of birth
                    </Paragraph>
                    <Paragraph textStyle="body-sm" css={{ paddingTop: 'tighter' }}>
                      {dob.monthOfBirth.value}/{dob.dayOfBirth.value}/{dob.yearOfBirth.value}
                    </Paragraph>
                  </>
                }
                dividers
                footer={
                  <ButtonGroup aria-label="modal actions">
                    <Modal.Close>
                      <TextButton>Edit information</TextButton>
                    </Modal.Close>
                    <Button emphasis="filled" tone="brand" onClick={submitAndContinue}>
                      Confirm
                    </Button>
                  </ButtonGroup>
                }
                trigger={
                  <Button emphasis="filled" disabled={enrollBtnDisabled}>
                    {enrollBtnText}
                  </Button>
                }
              />
              {skipButton}
              <Text textStyle="fineprint">
                By clicking &#34;Enroll and continue&#34; you agree to comply with{' '}
                <Anchor target="_blank" href="https://www.zillow.com/z/rental/rent-reporting-terms-and-conditions/">
                  these terms
                </Anchor>
                ...
              </Text>
            </>
          )}
          {enrollmentError && errorScreen}
        </S.PaymentReportingForm>
        <S.FAQAccordionContainer>
          <FaqComponent
            faqs={shortFaqs}
            showContactSection={false}
            actionButton={
              <Box css={{ display: 'flex', justifyContent: 'flex-end' }}>
                <TextButton onClick={onViewMoreFAQClick}>View more</TextButton>
              </Box>
            }
            onFAQClick={() => RachelCSEvents.emitOpenRentReportingFAQEvent({ user, currentProperty: { listingAlias } })}
          />
        </S.FAQAccordionContainer>
      </S.ContentBody>
      {!alreadyEnrolled && (
        <Modal
          size="md"
          open={faqModalOpen}
          trigger={null}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setFaqModalOpen(false);
            }
          }}
          body={<FaqComponent faqs={longFaqs} withBorder={false} />}
        />
      )}
    </>
  );
};

export default PaymentReportingOptInPage;
